import React, { useState, useEffect, useCallback } from 'react';
import { restaurantAPI } from '../../services/api';

function NotificationSettings() {
  const [settings, setSettings] = useState({
    orderNotifications: true,
    reviewNotifications: true,
    promotionalNotifications: false,
    soundEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // Track which setting is being saved
  const [savedKey, setSavedKey] = useState(null); // Track which setting was just saved
  const [error, setError] = useState(null);
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(true);
  const [autoAcceptSaving, setAutoAcceptSaving] = useState(false);

  useEffect(() => {
    // Load saved settings from backend
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const [notifResponse, autoAcceptResponse] = await Promise.all([
          restaurantAPI.getNotificationSettings(),
          restaurantAPI.getAutoAcceptSetting()
        ]);
        if (notifResponse.data.success && notifResponse.data.data.settings) {
          setSettings(notifResponse.data.data.settings);
        }
        if (autoAcceptResponse.data.success) {
          setAutoAcceptOrders(autoAcceptResponse.data.data.autoAcceptOrders ?? true);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        const savedSettings = localStorage.getItem('restaurantNotificationSettings');
        if (savedSettings) {
          setSettings(JSON.parse(savedSettings));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Auto-save function
  const saveSettings = useCallback(async (key, newSettings) => {
    setSavingKey(key);
    setError(null);

    try {
      const response = await restaurantAPI.updateNotificationSettings(newSettings);

      if (response.data.success) {
        // Also save to localStorage as backup/cache
        localStorage.setItem('restaurantNotificationSettings', JSON.stringify(newSettings));
        setSavedKey(key);
        setTimeout(() => setSavedKey(null), 1500);
      } else {
        setError('Failed to save. Please try again.');
        // Revert the toggle
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
      }
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      setError('Failed to save. Please try again.');
      // Revert the toggle on error
      setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    } finally {
      setSavingKey(null);
    }
  }, []);

  const handleToggle = useCallback((key) => {
    // Optimistically update the UI
    const newSettings = {
      ...settings,
      [key]: !settings[key]
    };
    setSettings(newSettings);
    setError(null);

    // Auto-save to backend
    saveSettings(key, newSettings);
  }, [settings, saveSettings]);

  const ToggleSwitch = ({ enabled, onToggle, isSaving, isSaved }) => (
    <button
      onClick={onToggle}
      disabled={isSaving}
      style={{
        width: '52px',
        height: '28px',
        borderRadius: '14px',
        border: 'none',
        background: isSaving ? '#94A3B8' : (enabled ? '#FF6961' : '#E2E8F0'),
        cursor: isSaving ? 'wait' : 'pointer',
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        outline: 'none',
        opacity: isSaving ? 0.7 : 1
      }}
    >
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '12px',
        background: 'white',
        position: 'absolute',
        top: '2px',
        left: enabled ? '26px' : '2px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px'
      }}>
        {isSaving && (
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid #E2E8F0',
            borderTopColor: '#FF6961',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
        )}
        {isSaved && !isSaving && <span style={{ color: '#10B981' }}>✓</span>}
      </div>
    </button>
  );

  const NotificationItem = ({ icon, title, description, enabled, onToggle, settingKey }) => {
    const isSaving = savingKey === settingKey;
    const isSaved = savedKey === settingKey;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        background: 'white',
        borderRadius: '12px',
        marginBottom: '12px',
        border: isSaved ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(226, 232, 240, 0.8)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: isSaving ? 'wait' : 'pointer'
      }}
      onMouseEnter={(e) => {
        if (!isSaving) {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      onClick={() => !isSaving && onToggle()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: enabled ? 'rgba(255, 105, 97, 0.1)' : '#F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontWeight: '600',
              fontSize: '16px',
              color: '#0F172A',
              marginBottom: '4px',
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {title}
              {isSaved && (
                <span style={{
                  fontSize: '12px',
                  color: '#10B981',
                  fontWeight: '500',
                  animation: 'fadeIn 0.3s ease'
                }}>
                  Saved!
                </span>
              )}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#64748B',
              lineHeight: '1.5'
            }}>
              {description}
            </div>
          </div>
        </div>
        <ToggleSwitch
          enabled={enabled}
          onToggle={(e) => { e.stopPropagation(); onToggle(); }}
          isSaving={isSaving}
          isSaved={isSaved}
        />
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{ background: '#F8FAFC', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#64748B' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(-10px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>

      <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          padding: '32px 24px',
          background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
          borderRadius: '16px',
          color: 'white',
          boxShadow: '0 4px 20px rgba(255, 105, 97, 0.2)'
        }} className="fade-in">
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '8px',
            letterSpacing: '-0.02em'
          }}>
            Notification Settings
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '15px',
            fontWeight: '400'
          }}>
            Manage how you receive notifications
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '13px',
            marginTop: '8px'
          }}>
            Changes are saved automatically
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="fade-in" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>❌</span>
            <span style={{ color: '#DC2626', fontWeight: '600', fontSize: '15px' }}>{error}</span>
          </div>
        )}

        {/* Auto Accept Orders */}
        <div className="fade-in" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          border: autoAcceptOrders ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#0F172A',
            letterSpacing: '-0.01em'
          }}>
            Order Acceptance
          </h3>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: autoAcceptOrders ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(226, 232, 240, 0.8)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>⚡</span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0F172A' }}>Auto Accept Orders</div>
                <div style={{ fontSize: '13px', color: '#64748B', marginTop: '2px' }}>
                  {autoAcceptOrders
                    ? 'Orders are automatically accepted after 30 seconds'
                    : 'You must manually accept or reject each order'}
                </div>
              </div>
            </div>
            <ToggleSwitch
              enabled={autoAcceptOrders}
              onToggle={async () => {
                const newValue = !autoAcceptOrders;
                setAutoAcceptSaving(true);
                try {
                  const response = await restaurantAPI.updateAutoAcceptSetting({ autoAcceptOrders: newValue });
                  if (response.data.success) {
                    setAutoAcceptOrders(newValue);
                  }
                } catch (err) {
                  console.error('Failed to update auto-accept:', err);
                  setError('Failed to update auto-accept setting');
                } finally {
                  setAutoAcceptSaving(false);
                }
              }}
              isSaving={autoAcceptSaving}
              isSaved={false}
            />
          </div>
        </div>

        {/* Order Notifications */}
        <div className="fade-in" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#0F172A',
            letterSpacing: '-0.01em'
          }}>
            Order Notifications
          </h3>

          <NotificationItem
            icon="📦"
            title="Order Notifications"
            description="Get notified about new orders, order updates, and cancellations"
            enabled={settings.orderNotifications}
            onToggle={() => handleToggle('orderNotifications')}
            settingKey="orderNotifications"
          />
        </div>

        {/* Review Notifications */}
        <div className="fade-in" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#0F172A',
            letterSpacing: '-0.01em'
          }}>
            Feedback Notifications
          </h3>

          <NotificationItem
            icon="⭐"
            title="Review Notifications"
            description="Get notified when customers leave reviews for your restaurant"
            enabled={settings.reviewNotifications}
            onToggle={() => handleToggle('reviewNotifications')}
            settingKey="reviewNotifications"
          />
        </div>

        {/* Marketing Notifications */}
        <div className="fade-in" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#0F172A',
            letterSpacing: '-0.01em'
          }}>
            Marketing
          </h3>

          <NotificationItem
            icon="📢"
            title="Promotional Notifications"
            description="Receive updates about promotions, tips, and platform news"
            enabled={settings.promotionalNotifications}
            onToggle={() => handleToggle('promotionalNotifications')}
            settingKey="promotionalNotifications"
          />
        </div>

        {/* Sound Settings */}
        <div className="fade-in" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#0F172A',
            letterSpacing: '-0.01em'
          }}>
            Sound Settings
          </h3>

          <NotificationItem
            icon="🔊"
            title="Sound Enabled"
            description="Play a sound when receiving new notifications"
            enabled={settings.soundEnabled}
            onToggle={() => handleToggle('soundEnabled')}
            settingKey="soundEnabled"
          />
        </div>

        {/* Note */}
        <div style={{
          textAlign: 'center',
          padding: '20px',
          background: 'rgba(100, 116, 139, 0.05)',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <p style={{
            margin: 0,
            color: '#64748B',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Note: Browser notifications may require additional permissions.
            Check your browser settings if notifications aren't working.
          </p>
        </div>
      </div>
    </div>
  );
}

export default NotificationSettings;
